// DetailsForm — /details/:token
//
// The no-login page a client lands on from a "finish your details" email.
// Renders ONLY the fields staff asked for. Deliberately short: someone who
// already did a full intake will abandon anything that looks like starting over.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";

const LABELS: Record<string, string> = {
  phone: "Phone number",
  annual_income: "Yearly income before taxes",
  income_source: "Where does your income come from?",
  employer_name: "Employer (optional)",
  employment_years: "Years at this job (optional)",
  housing_status: "Do you rent or own?",
  housing_payment: "Monthly rent or mortgage payment",
};
const INCOME_SOURCES = [
  ["employed", "A job (W-2)"],
  ["self_employed", "Self-employed / 1099"],
  ["retired", "Retirement / pension"],
  ["benefits", "Disability / Social Security / benefits"],
  ["other", "Other"],
];
const HOUSING = [
  ["rent", "Rent"],
  ["own_mortgage", "Own with a mortgage"],
  ["own_outright", "Own outright"],
  ["other", "Other"],
];
const OPTIONAL = new Set(["employer_name", "employment_years"]);

export default function DetailsFormPage() {
  const { token } = useParams();
  const [state, setState] = useState<any>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`/api/details/${token}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.message || "That link is not valid.");
        setState(d);
        if (d.completed) setDone(true);
        const seed: Record<string, string> = {};
        for (const f of d.fields || []) {
          const k = d.known?.[f];
          if (k !== null && k !== undefined) seed[f] = String(k);
        }
        setVals(seed);
      })
      .catch((e) => setErr(e.message));
  }, [token]);

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`/api/details/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vals),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.message || "Could not save that.");
      setDone(true);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const wrap = (children: React.ReactNode) => (
    <div className="landing" style={{ minHeight: "100vh", display: "grid", placeItems: "start center", padding: "40px 18px 70px" }}>
      <div className="landing-card" style={{ width: "min(560px, 100%)", padding: 30 }}>{children}</div>
    </div>
  );

  if (err && !state) return wrap(<><h1 style={{ fontSize: 22, marginBottom: 8 }}>Link problem</h1><p className="muted">{err}</p></>);
  if (!state) return wrap(<p className="muted">Loading…</p>);

  if (done) return wrap(
    <>
      <CheckCircle2 size={34} aria-hidden="true" style={{ color: "var(--ne-success, #2c8a3e)", marginBottom: 10 }} />
      <h1 style={{ fontSize: 23, marginBottom: 8 }}>Got it — thank you.</h1>
      <p className="muted">
        That's everything we needed. {state.company} will be in touch with your options.
        You can close this page.
      </p>
    </>
  );

  const fields: string[] = state.fields || [];
  const missingRequired = fields.some((f) => !OPTIONAL.has(f) && !String(vals[f] ?? "").trim());

  return wrap(
    <>
      <p className="landing-kicker" style={{ marginBottom: 6 }}>{state.company}</p>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>A few quick details, {state.first_name}</h1>
      <p className="muted small" style={{ marginBottom: 6 }}>
        These are the things lenders ask for first. It takes about a minute.
      </p>
      {state.note && <p className="muted small" style={{ marginBottom: 6 }}>{state.note}</p>}
      <p className="muted small" style={{ marginBottom: 22 }}>
        <ShieldCheck size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 5 }} />
        Private to your file. We never sell or share it.
      </p>

      <div className="stacked-form">
        {fields.map((f) => (
          <label className="form-field" key={f}>
            <span>{LABELS[f] || f}</span>
            {f === "income_source" ? (
              <select value={vals[f] ?? ""} onChange={(e) => setVals({ ...vals, [f]: e.target.value })}>
                <option value="">Select…</option>
                {INCOME_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : f === "housing_status" ? (
              <select value={vals[f] ?? ""} onChange={(e) => setVals({ ...vals, [f]: e.target.value })}>
                <option value="">Select…</option>
                {HOUSING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <input
                inputMode={f === "phone" ? "tel" : f.includes("income") || f.includes("payment") || f.includes("years") ? "numeric" : "text"}
                placeholder={f === "phone" ? "(803) 555-0142" : f === "annual_income" ? "$48,000" : f === "housing_payment" ? "$1,200" : ""}
                value={vals[f] ?? ""}
                onChange={(e) => setVals({ ...vals, [f]: e.target.value })}
              />
            )}
            {f === "annual_income" && (
              <span className="muted small">Before taxes. Include every source — job, self-employment, benefits, support.</span>
            )}
          </label>
        ))}
      </div>

      {err && <p className="error-text" style={{ marginTop: 12 }}>{err}</p>}
      <button className="primary-button" style={{ width: "100%", justifyContent: "center", marginTop: 18 }}
        disabled={busy || missingRequired} onClick={submit}>
        {busy ? "Saving…" : "Send it in"}
      </button>
    </>
  );
}
